import {HttpClient, HttpContext, HttpHeaders} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {AdditionalPACData, PacUpdateRequest, PaidPaymentCollection} from '../../models/additional-pac-data.model';
import {PaymentBlockService} from '../../services/payment-block.service';
import {SKIP_LOADER} from '../http-context-tokens';

@Injectable({
  providedIn: 'root'
})
export class PacService {
  constructor(private readonly http: HttpClient, private readonly paymentBlockService: PaymentBlockService) {}

  fetchAdditionalInfo(pacNumber: string, isFromCbs = false): Observable<AdditionalPACData> {
    const params = { pacNumber, isFromCbs: isFromCbs.toString()  };
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.get<AdditionalPACData>('api/additional-pac-info', { params, headers }).pipe(
      tap(data => {
        if (data?.blocked && data.retryAfterSeconds) {
          this.paymentBlockService.notifyBlocked(data.pacNumber ?? pacNumber, data.retryAfterSeconds);
        }
      })
    );
  }

  /**
   * Same lookup as {@link fetchAdditionalInfo}, but silent - does not re-trigger the block popup.
   * Used by AppComponent to poll a PAC that's already showing the popup, so it can detect the
   * block clearing early without resetting the countdown on every poll tick.
   */
  checkBlockStatus(pacNumber: string): Observable<AdditionalPACData> {
    const params = { pacNumber, isFromCbs: 'false' };
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    const context = new HttpContext().set(SKIP_LOADER, true);
    return this.http.get<AdditionalPACData>('api/additional-pac-info', { params, headers, context });
  }

  saveAdditionalInfo(additionalPACData: AdditionalPACData): Observable<any> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.post('api/additional-pac-info', additionalPACData, { headers });
  }

  updateAdditionalInfo(pacUpdateRequest: PacUpdateRequest): Observable<any > {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    return this.http.patch('api/additional-pac-info', pacUpdateRequest, { headers });
  }

  addPaymentCollectionToPac(paymentCollectionDTO: PaidPaymentCollection): Observable<any> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.post('api/additional-pac-info/payment-info', paymentCollectionDTO, { headers });
  }

  initiatePayment(paymentData: any): Observable<any> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.http.post('api/payment/initiate', paymentData, { headers });
  }

  sendSessionStatus(paymentReference: string, status: "EXPIRED" | "ABORTED" | "CLOSED"): Observable<any> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    const body = {
      paymentReference,
      status
    };
    return this.http.post('api/session/status', body, { headers });
  }

}
